import type { LiveTranscriptTurn } from "./live";
import type { MagnusChatOutcome } from "./magnus-chat-tool";

export type LiveCallPhase =
  | "idle"
  | "connecting"
  | "listening"
  | "user-speaking"
  | "thinking"
  | "speaking"
  | "delegating"
  | "switching"
  | "errored";

export function phaseLabel(phase: LiveCallPhase, muted: boolean): string {
  switch (phase) {
    case "connecting":
      return "Connecting";
    case "thinking":
      return "Thinking";
    case "speaking":
      return "Speaking";
    case "delegating":
      return "Asking Magnus…";
    case "switching":
      return "Switching voice…";
    default:
      return muted ? "Muted" : "Listening";
  }
}

/**
 * What a screen reader hears as the call changes: the moments that matter,
 * not the listening/speaking flips that happen several times a second.
 * Empty while there is no call.
 */
export function callAnnouncement(
  phase: LiveCallPhase,
  muted: boolean,
  delegatingQuery: string | null
): string {
  switch (phase) {
    case "idle":
    case "errored":
      return "";
    case "connecting":
      return "Connecting voice call";
    case "delegating":
      return delegatingQuery
        ? `Asking Magnus: ${delegatingQuery}`
        : "Asking Magnus";
    case "switching":
      return "Switching voice";
    default:
      return muted ? "On a voice call, muted" : "On a voice call";
  }
}

export type LevelBarsMode = "input" | "output" | "busy";

/** Whose level the pill's bars show: the voice while it speaks, nobody
 *  (a pulse) while the call works, the user otherwise. */
export function levelBarsMode(phase: LiveCallPhase): LevelBarsMode {
  switch (phase) {
    case "connecting":
    case "thinking":
    case "delegating":
    case "switching":
      return "busy";
    case "speaking":
      return "output";
    default:
      return "input";
  }
}

const LEVEL_BAR_WEIGHTS = [0.6, 1, 0.8, 0.5] as const;

/** Each bar's vertical scale, 0.25 at rest. */
export function levelBarScales(
  mode: LevelBarsMode,
  levels: { inputLevel: number; outputLevel: number },
  muted: boolean
): number[] {
  const level =
    mode === "output" ? levels.outputLevel : muted ? 0 : levels.inputLevel;
  const amplitude =
    mode === "busy" ? 0 : Math.min(1, Math.sqrt(Math.max(0, level)) * 1.2);
  return LEVEL_BAR_WEIGHTS.map((weight) => 0.25 + amplitude * weight * 0.55);
}

export const CALL_ENDED_MESSAGE = "The call ended.";

/** The captions line: the running request, else the newest speech. Muted
 *  unless it's the voice talking. */
export function captionLine(
  captions: { user: string; assistant: string },
  delegatingQuery: string | null
): { text: string; muted: boolean } {
  const assistant = captionTail(captions.assistant);
  const text = delegatingQuery || assistant || captionTail(captions.user);
  return {
    text: text || "Captions appear as you talk.",
    muted: Boolean(delegatingQuery) || !assistant,
  };
}

export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

const CAPTION_MAX_CHARS = 90;

function captionTail(text: string): string {
  const trimmed = text.trim().replace(/\s+/g, " ");
  if (trimmed.length <= CAPTION_MAX_CHARS) {
    return trimmed;
  }
  const cut = trimmed.slice(-CAPTION_MAX_CHARS);
  const space = cut.indexOf(" ");
  return `…${space > 0 ? cut.slice(space + 1) : cut}`;
}

export type CallTurn<D> = {
  role: "user" | "assistant";
  text: string;
  at: string;
  delegations?: D[];
};

/**
 * The spoken turns as the recap stores them, with each `magnus_chat`
 * delegation attached to the user turn that was being spoken when it
 * started. A delegation with no such turn gets an empty user turn.
 */
export function callTranscript<D>(
  spoken: readonly LiveTranscriptTurn[],
  delegations: Iterable<{ atMs: number; marker: D }>,
  sessionEpoch: number
): CallTurn<D>[] {
  const turns: CallTurn<D>[] = spoken.map((t) => ({
    role: t.role,
    text: t.text,
    at: new Date(sessionEpoch + t.startMs).toISOString(),
  }));
  for (const { atMs, marker } of delegations) {
    const index = spoken.findLastIndex(
      (t) => t.role === "user" && t.startMs <= atMs
    );
    const target = turns[index];
    if (target) {
      target.delegations = [...(target.delegations ?? []), marker];
    } else {
      turns.push({
        role: "user",
        text: "",
        at: new Date(sessionEpoch + atMs).toISOString(),
        delegations: [marker],
      });
    }
  }
  return turns;
}

export type EngineStatus = "ready" | "submitted" | "streaming" | "error";

export const isBusyStatus = (status: EngineStatus) =>
  status === "submitted" || status === "streaming";

export type BridgeMessage = {
  id: string;
  role: string;
  parts: readonly unknown[];
};

/** A message's text parts, one per line. */
export function partsText(parts: readonly unknown[]): string {
  return parts
    .map((part) => {
      const p = part as { type?: unknown; text?: unknown };
      return p.type === "text" && typeof p.text === "string" ? p.text : "";
    })
    .filter(Boolean)
    .join("\n")
    .trim();
}

function pendingApproval(message: BridgeMessage): string | null {
  for (const part of message.parts) {
    const p = part as {
      type?: unknown;
      data?: { summary?: string; toolName?: string };
    };
    if (p.type === "data-approval-request") {
      const summary = p.data?.summary?.replaceAll("**", "").trim();
      return summary || p.data?.toolName || "an action";
    }
  }
  return null;
}

/**
 * The outcome of a delegated turn once the chat has settled, or null while
 * it is still working. A failure is retryable only while no reply exists,
 * so resubmitting cannot duplicate one.
 */
export function settleDelegatedTurn(
  messages: readonly BridgeMessage[],
  status: EngineStatus,
  userMessageId: string,
  sawBusy: boolean
): MagnusChatOutcome | null {
  const index = messages.findIndex((m) => m.id === userMessageId);
  if (index < 0) {
    return status === "error"
      ? {
          kind: "failed",
          message: "the request didn't go through",
          retryable: true,
        }
      : null;
  }
  const reply = messages
    .slice(index + 1)
    .findLast((m) => m.role === "assistant");
  if (reply) {
    const approval = pendingApproval(reply);
    if (approval) {
      return {
        kind: "awaiting-approval",
        summary: approval,
        messageId: reply.id,
      };
    }
  }
  if (status === "error") {
    return reply
      ? {
          kind: "failed",
          message: "the chat hit an error",
          messageId: reply.id,
        }
      : { kind: "failed", message: "the chat hit an error", retryable: true };
  }
  if (!sawBusy || isBusyStatus(status)) {
    return null;
  }
  return reply
    ? { kind: "answered", text: partsText(reply.parts), messageId: reply.id }
    : { kind: "failed", message: "no reply came back", retryable: true };
}
